// Client-side mirrors of the API response shapes (the API is the source of truth).
export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  roles: string[];
  permissions: string[];
}

export interface Citation {
  title: string;
  kbId: string;
  category: string;
  url?: string | null;
}

export interface FoxitAnswer {
  answer: string | null;
  deflected: boolean;
  citations: Citation[];
  handoff: { category: string; inbox: string };
  kbVersion: string | null;
  traceId?: string | null;
}

export type Severity = 'BLOCK' | 'WARN' | 'NOTE';

export interface ChangeItem {
  id: string;
  category: string;
  severity: Severity;
  before: string;
  after: string;
  reason: string;
  fixed: boolean;
}

export interface StageResult {
  stage: string;
  status: 'ok' | 'skipped' | 'blocked' | 'error';
  detail?: string;
}

export type DocFormat = 'docx' | 'pptx' | 'pdf' | 'html' | 'md';

export interface EmptySection {
  index: number;
  reason: string;
}

export interface UploadResult {
  filename: string;
  text: string;
  sourceFormat: DocFormat | null;
  byteSize: number;
  originalFileBase64: string | null;
  emptySections?: EmptySection[];
  adeStatus?: 'processing' | 'done' | 'failed' | null;
  uploadId?: string | null;
}

export interface StandardizeInput {
  title: string;
  author: string;
  version: string;
  department: string;
  purpose: string;
  format: DocFormat;
  content: string;
  sourceFormat: DocFormat | null;
  originalFileBase64: string | null;
  promptInstructions?: string;
}

export interface StandardizeResult {
  docId: string;
  title: string;
  format: DocFormat;
  verdict: 'pass' | 'blocked';
  passed: boolean;
  summary: { block: number; warn: number; note: number };
  changes: ChangeItem[];
  correctedText: string;
  unappliedCount: number;
  stages: StageResult[];
  download: { id: string; filename: string; byteSize: number };
  traceId?: string | null;
  traceUrl?: string | null;
}

export interface AdminOverview {
  standardizerRuns: number;
  foxitAnswered: number;
  foxitDeflected: number;
  downloads: number;
  activeUsers: number;
  kbLiveVersion: string | null;
  kbDocCount: number;
}
export interface AuditRow {
  seq: number;
  at: string;
  actor_email: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
}
export interface KbVersionRow {
  label: string;
  live: boolean;
  created_at: string;
  docs: number;
  chunks: number;
}
export interface AdminUserRow {
  id: string;
  email: string;
  display_name: string;
  status: string;
  roles: string[];
}

export interface AdminUsageRow {
  id: string;
  email: string;
  display_name: string;
  status: string;
  roles: string[];
  token_usage: number;
  total_cost: number;
}

export interface UsageSummary {
  total_tokens: number;
  total_cost: number;
}

export interface PaginatedUsageResponse extends PaginatedResponse<AdminUsageRow> {
  summary: UsageSummary;
}


/** Returned by PATCH /admin/users/:id — same row plus resolved permissions. */
export interface AdminUserDetail extends AdminUserRow {
  permissions: string[];
}

export interface AdminSettings {
  provider: string;
  modelStandard: string;
  modelPremium: string;
  spendLimitUsd: number;
  rateLimit: number;
  rateWindowSeconds: number;
  spentUsd: number;
  aiCalls: number;
  aiTokens: number;
  flaggedSecrets: number;
  flaggedPii: number;
}

export interface AdminSettingsPatch {
  spendLimitUsd?: number;
  rateLimit?: number;
  rateWindowSeconds?: number;
}

export interface AdminUserPatch {
  status?: string;
  roles?: string[];
}

export interface AdminUserCreate {
  email: string;
  roles: string[];
  status?: string;
}

export const ALL_ROLES = ['owner', 'admin', 'editor', 'staff'] as const;
export const USER_STATUSES = ['active', 'invited', 'disabled'] as const;
export interface GapRow {
  id: string;
  question: string;
  rating: number;
  deflected: boolean;
  comment: string | null;
  created_at: string;
  // Link to this question's OpenTelemetry trace in Langfuse, when one exists.
  traceUrl: string | null;
}

export interface LibraryCategory {
  id: string;
  name: string;
  sort: number;
}
export interface LibraryFile {
  id: string;
  title: string;
  filename: string;
  byte_size: number;
  content_type: string;
  created_at: string;
  category_id: string;
  category: string;
  has_empty_sections?: boolean;
  empty_sections_detail?: any;
  extracted_markdown?: string;
}

export interface DownloadFile {
  id: string;
  owner_id: string;
  filename: string;
  byte_size: number;
  content_type: string;
  created_at: string;
  file_path: string;
  user_role: string | null;
  owner_name?: string;
  archived?: boolean;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}
